import CreateQuotePDF from "@/components/CreateQuotePDF/createQuotePDF.component";
import { FullSingleQuote } from "@/types";
import { pdf } from "@react-pdf/renderer";

/**
 * Generates a PDF from the quote data and returns it as a base64 string
 * @param quote - The quote data to generate the PDF from
 * @returns Promise that resolves to base64 string of the generated PDF
 */
export const generateQuotePDFAsBase64 = async (
  quote: FullSingleQuote
): Promise<string> => {
  try {
    // Generate the PDF document using the CreateQuotePDF component
    const pdfDocument = pdf(<CreateQuotePDF quote={quote} />);

    // Generate the PDF blob
    const pdfBlob = await pdfDocument.toBlob();

    // Convert blob to base64 string for the ViewPDF component
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove the data:application/pdf;base64, prefix to get just the base64 string
        const base64String = (reader.result as string).split(",")[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });
  } catch (error) {
    console.error("Failed to generate PDF:", error);
    throw new Error("Failed to generate PDF document");
  }
};

/**
 * Generates a PDF from the quote data and returns it as a blob
 * @param quote - The quote data to generate the PDF from
 * @returns Promise that resolves to a Blob of the generated PDF
 */
export const generateQuotePDFAsBlob = async (
  quote: FullSingleQuote
): Promise<Blob> => {
  try {
    // Generate the PDF document using the CreateQuotePDF component
    const pdfDocument = pdf(<CreateQuotePDF quote={quote} />);

    // Return the PDF blob directly for cases where blob is preferred
    return await pdfDocument.toBlob();
  } catch (error) {
    console.error("Failed to generate PDF blob:", error);
    throw new Error("Failed to generate PDF document");
  }
};
